import { useEffect, useState } from 'react';
import { Form, Input, Button, Select } from 'antd';
import { useUser } from '../../store/userContext';
import { useRole } from '../../store/roleContext';
import { User } from '../../types/user';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

interface UserFormProps {
  editUser?: User;
  onFinishEdit?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ editUser, onFinishEdit }) => {
  const [form] = Form.useForm();
  const { addUser, updateUser } = useUser();
  const { roles, listRoles } = useRole();
  const [selectedRole, setSelectedRole] = useState<string | undefined>();
  const { t } = useTranslation();

  useEffect(() => {
    listRoles();
    if (editUser) {
      form.setFieldsValue({
        ...editUser,
        roles: typeof editUser.roles === 'object' ? editUser.roles : editUser.roles,
      });
    }
  }, [editUser]);

  const onFinish = (values: any) => {
    const user: User = {
      ...editUser,
      ...values,
      id: editUser?.id || Date.now(),
    };
    editUser ? updateUser(user) : addUser(user);
    form.resetFields();
    onFinishEdit?.();
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        name="name"
        label={t('name')}
        rules={[{ required: true, message: t('please_enter_name') || '' }]}
      >
        <Input placeholder={t('name') || ''} />
      </Form.Item>

      <Form.Item
        name="email"
        label={t('email')}
        rules={[
          { required: true, message: t('please_enter_email') || '' },
          { type: 'email', message: t('please_enter_valid_email') || '' }
        ]}
      >
        <Input placeholder={t('email') || ''} />
      </Form.Item>

      <Form.Item
        name="password"
        label={t('password')}
        rules={[{ required: true, message: t('please_enter_password') || '' }]}
      >
        <Input.Password placeholder={t('password') || ''} />
      </Form.Item>

      <Form.Item
        name="roles"
        label={t('role')}
        rules={[{ required: true, message: t('please_select_role') || '' }]}
      >
        <Select
          placeholder={t('select_role') || ''}
          onChange={(val) => setSelectedRole(val)}
        >
          {roles.map((role) => (
            <Option key={role.id} value={role.id}>
              {role.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          {editUser ? t('update_user') : t('create_user')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default UserForm;
